import React from 'react';
import {
  CheckCircle as CheckCircleIcon,
  ContentCopy as ContentCopyIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

const HOW_TO_STEPS: { label: string; text: React.ReactNode }[] = [
  {
    label: '1',
    text: (
      <>
        Share your link however you want. Check the &quot;Telehealth (virtual session)&quot;
        box on a JotPsych appointment to send it with the reminder (patient needs a phone
        or email on file), or paste it into any calendar, email, or text.
      </>
    ),
  },
  {
    label: '2',
    text: <>When your patient arrives, they&apos;ll check in using your link and run a quick device test.</>,
  },
  {
    label: '3',
    text: <>You&apos;ll see them in your waiting-room queue. One click lets them in and starts the call.</>,
  },
];

export interface JotMeetActiveViewProps {
  /** Practice admin gets the "Manage in My Practice > Clinicians" footer note. */
  isPracticeAdmin: boolean;
  /** The provider's waiting-room shareable URL. */
  waitingRoomUrl: string | null;
  /** Display name patients see in the waiting room. */
  displayName: string | null;
  isLoadingLink: boolean;
  isRefetchingLink: boolean;
  linkError: boolean;
  onCopyLink: () => void;
  onRetryLink: () => void;
  onUpdateCommunicationsName: () => void;
}

/**
 * The "entitled" state of the JotMeet settings page.
 *
 * Composes:
 *   1. "JotMeet is active" status banner
 *   2. Waiting-room link card (preserves the existing check-in details /
 *      communications-name editor)
 *   3. "How to use it" numbered steps
 *   4. Role-conditional subscription footer note
 */
export const JotMeetActiveView: React.FC<JotMeetActiveViewProps> = ({
  isPracticeAdmin,
  waitingRoomUrl,
  displayName,
  isLoadingLink,
  isRefetchingLink,
  linkError,
  onCopyLink,
  onRetryLink,
  onUpdateCommunicationsName,
}) => (
  <Stack spacing={3}>
    {/* Active status banner */}
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        // theme.palette.primary[50] isn't registered, so derive a tinted
        // bg from primary.main via alpha() to get the lavender we want.
        bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
        borderRadius: 2,
        px: 2.5,
        py: 2,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'common.white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <CheckCircleIcon />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          JotMeet is active
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Your waiting room is ready to use.
        </Typography>
      </Box>
      <Chip
        label="Active"
        size="small"
        sx={{
          bgcolor: 'common.white',
          color: 'primary.main',
          fontWeight: 600,
          flexShrink: 0,
        }}
      />
    </Box>

    {/* Waiting-room link card */}
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2.5,
      }}
    >
      <Typography
        variant="overline"
        sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 1 }}
      >
        Your waiting-room link
      </Typography>

      {linkError ? (
        <Alert
          severity="error"
          sx={{ mt: 1 }}
          action={
            <Button color="inherit" size="small" onClick={onRetryLink}>
              Retry
            </Button>
          }
        >
          Unable to load your waiting room settings. Please try again.
        </Alert>
      ) : (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ mt: 1, alignItems: { xs: 'stretch', sm: 'center' } }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1.25,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              fontFamily: 'monospace',
              fontSize: '0.95rem',
            }}
          >
            <LinkIcon sx={{ color: 'text.secondary', fontSize: 18, flexShrink: 0 }} />
            <Box
              component="span"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {isLoadingLink ? 'Loading…' : (waitingRoomUrl ?? '—')}
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<ContentCopyIcon />}
            onClick={onCopyLink}
            disabled={!waitingRoomUrl}
            sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0, minWidth: 110 }}
          >
            Copy
          </Button>
        </Stack>
      )}

      {/* Check-in details — preserved from the original page */}
      {displayName && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2">
            People checking in will see you as:{' '}
            <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
              {displayName}
            </Typography>
          </Typography>
          <Button
            variant="text"
            size="small"
            onClick={onUpdateCommunicationsName}
            sx={{ textTransform: 'none', mt: 0.5, px: 0 }}
          >
            Update Communications Name
          </Button>
        </Box>
      )}

      {isRefetchingLink && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
          Refreshing waiting room settings...
        </Typography>
      )}
    </Box>

    {/* How to use it */}
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
        How to use it
      </Typography>
      <Stack spacing={1.5}>
        {HOW_TO_STEPS.map(step => (
          <Stack key={step.label} direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: '0.75rem',
                flexShrink: 0,
                mt: 0.25,
              }}
            >
              {step.label}
            </Box>
            <Typography variant="body2">{step.text}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>

    {/* Subscription management footer note — role-dependent copy */}
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        px: 2.5,
        py: 1.5,
      }}
    >
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {isPracticeAdmin
          ? 'Manage your subscriptions in My Practice > Clinicians.'
          : 'Your practice administrator manages subscriptions.'}
      </Typography>
    </Box>
  </Stack>
);
